import React, { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { cx } from '@/utils';
import CarouselDots from '@common/CarouselDots';
import CustomLink from '@common/CustomLink';
import { useDotButton } from '@common/DotButton/DotButton';
import { useLayoutContext } from '@common/Layout/LayoutProvider/LayoutContext';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';

import { PageCorporateLogosBlock } from '../../Providers/GraphqlProvider/generated';
import styles from './styles.module.css';

// Show 3 items in viewport on mobile, but scroll one item per interaction
const MOBILE_SLIDES_TO_SCROLL = 1;
const DESKTOP_SLIDES_TO_SCROLL = 5;

const FooterLogos = ({
  currentPageCorporateLogosBlock,
}: {
  currentPageCorporateLogosBlock?: PageCorporateLogosBlock;
}) => {
  const {
    layout: { pageCorporateLogosBlock: pageCorporateLogosBlockContext },
  } = useLayoutContext();

  const pageCorporateLogosBlock = currentPageCorporateLogosBlock || pageCorporateLogosBlockContext;

  const [autoplay, setAutoplay] = useState<any>([]);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      slidesToScroll: MOBILE_SLIDES_TO_SCROLL,
      breakpoints: {
        '(min-width: 768px)': {
          slidesToScroll: DESKTOP_SLIDES_TO_SCROLL,
        },
      },
    },
    [...autoplay]
  );

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);

  useEffect(() => {
    if (pageCorporateLogosBlock && pageCorporateLogosBlock?.items.length > 1) {
      setAutoplay([Autoplay({ active: true })]);
    }
  }, [pageCorporateLogosBlock]);

  const itemsLogo = useMemo(() => {
    if (!pageCorporateLogosBlock?.items) return null;
    const items = [...pageCorporateLogosBlock.items];
    items.sort((a: any, b: any) => a.orderIndex - b.orderIndex);
    return items;
  }, [pageCorporateLogosBlock]);

  if (pageCorporateLogosBlock?.isDisabled) return null;

  return (
    <section className={cx(styles.logos, 'inner')}>
      <div className="w-full overflow-hidden" ref={emblaRef}>
        <div className="flex w-full items-center justify-between gap-2">
          {itemsLogo?.map((logo, key) => {
            if (!logo.corporateLogo) return null;
            return (
              <div className="min-w-0 flex-[0_0_calc(100%/3)] md:flex-[0_0_20%]" key={key}>
                <div className={'relative w-full md:start-10'}>
                  <CustomLink key={key} pageLink={logo.linkItem}>
                    <div className={styles.slide}>
                      <Image
                        src={logo.corporateLogo.downloadUrl!}
                        alt={logo.corporateLogoName || ''}
                        width={184}
                        height={102}
                        className="h-[60px] w-[93px] object-cover md:h-[102px] md:w-[184px]"
                      />
                    </div>
                  </CustomLink>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-3 flex w-full justify-center md:hidden">
        <CarouselDots
          scrollSnaps={scrollSnaps}
          selectedIndex={selectedIndex}
          onDotClick={onDotButtonClick}
          dotClassName="dot !h-2 !w-2 !bg-silver !mx-1"
          selectedClassName="!bg-primaryDark"
        />
      </div>
    </section>
  );
};

export default FooterLogos;
